import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { apiFetch } from "../../../context/AuthContext";

const STORAGE_KEY = "payrollTemplateBuilder:lastSelection";

export const useTemplateBuilder = ({
  isOpen,
  t,
  showAlert,
  modulePermissions = {},
  userRole = "",
}) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  const [catalog, setCatalog] = useState({
    variants: ["net", "gross", "days_net", "days_gross"],
    locked: [
      { key: "clientCode", label: "clientCode", labelKey: "clientCode", kind: "neutral" },
      { key: "date", label: "date", labelKey: "date", kind: "neutral" },
    ],
    types: [],
    totals: { types: 0, columns: 0, allowanceColumns: 0, deductionColumns: 0 },
  });
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  const [presets, setPresets] = useState([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [activePresetId, setActivePresetId] = useState(null); // null = Full template

  // Selection state
  const [selectedTypes, setSelectedTypes] = useState({}); // { [typeKey]: ["net", "gross", ...] }
  const [customColumns, setCustomColumns] = useState([]); // [ { label, kind, variants, key, derivedType } ]
  const [isModified, setIsModified] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("all"); // "all" | "allowance" | "deduction"

  const isAdmin = ["admin", "superadmin"].includes(userRole);
  const canShareCompany = isAdmin || Boolean(modulePermissions?.template || modulePermissions?.export_template);

  // Helper to initialize full selection from catalog
  const createFullSelection = useCallback((typesList) => {
    const full = {};
    (typesList || []).forEach((t) => {
      full[t.key] = [...(t.availableVariants || ["net", "gross"])];
    });
    return full;
  }, []);

  // Clips a raw {typeKey: variants[]} selection map down to variants that
  // are still valid for each type in the given catalog, and drops types
  // that no longer exist at all. A selection restored from localStorage or
  // a saved preset can predate a catalog change (e.g. Overtime Allowance
  // moving from 4 variants to 2 once merged with the linked Overtime
  // Days/Hours columns) — without this, phantom variants would silently
  // inflate the totals shown in the Template Builder.
  const sanitizeTypesSelection = useCallback((rawMap, catalogTypesList) => {
    const validVariantsByKey = new Map(
      (catalogTypesList || []).map((t) => [t.key, new Set(t.availableVariants || [])])
    );
    const clean = {};
    Object.entries(rawMap || {}).forEach(([key, variants]) => {
      const validSet = validVariantsByKey.get(key);
      if (!validSet) return;
      const filtered = (Array.isArray(variants) ? variants : []).filter((v) => validSet.has(v));
      if (filtered.length > 0) clean[key] = filtered;
    });
    return clean;
  }, []);

  // Fetch catalog
  const fetchCatalog = useCallback(async () => {
    setIsLoadingCatalog(true);
    setCatalogError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/payrollX/bulk/allowances-deductions-template/columns`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.success && json.data) {
        setCatalog(json.data);
        return json.data;
      } else {
        throw new Error(json.error || "Failed to parse catalog");
      }
    } catch (err) {
      console.error("Error fetching template columns catalog:", err);
      setCatalogError(err.message);
      showAlert(t("error") || "Error", t("errorLoadingColumns") || "Could not load the column list.", "error");
      return null;
    } finally {
      setIsLoadingCatalog(false);
    }
  }, [API_BASE_URL, showAlert, t]);

  // Fetch presets
  const fetchPresets = useCallback(async () => {
    setIsLoadingPresets(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/payrollX/template-presets`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setPresets(json.data);
          return json.data;
        }
      }
    } catch (err) {
      console.error("Error fetching presets:", err);
    } finally {
      setIsLoadingPresets(false);
    }
    return [];
  }, [API_BASE_URL]);

  // Reset to full template
  const resetToFullTemplate = useCallback(() => {
    setSelectedTypes(createFullSelection(catalog.types));
    setCustomColumns([]);
    setActivePresetId(null);
    setIsModified(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }, [catalog.types, createFullSelection]);

  // Apply a preset
  const applyPreset = useCallback((preset) => {
    if (!preset) {
      resetToFullTemplate();
      return;
    }

    const selection = preset.selection || {};
    const newTypes = {};
    if (Array.isArray(selection.types)) {
      selection.types.forEach((item) => {
        if (item?.key) {
          newTypes[item.key] = Array.isArray(item.variants) ? [...item.variants] : ["net", "gross"];
        }
      });
    }
    setSelectedTypes(sanitizeTypesSelection(newTypes, catalog.types));
    setCustomColumns(Array.isArray(selection.customColumns) ? selection.customColumns : []);
    setActivePresetId(preset.id);
    setIsModified(false);
  }, [resetToFullTemplate, sanitizeTypesSelection, catalog.types]);

  // Keep latest callback references without making them effect dependencies —
  // fetchCatalog/fetchPresets/applyPreset/createFullSelection are recreated on
  // every render (they close over props like `t`/`showAlert` that the parent
  // re-creates each render too), so depending on them directly caused this
  // effect to re-fire in a tight loop and hammer the backend endpoints.
  const latestRef = useRef({});
  latestRef.current = { fetchCatalog, fetchPresets, applyPreset, createFullSelection };

  // Initial load when modal opens — runs exactly once per open cycle.
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasLoadedRef.current = false;
      return;
    }
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    let isMounted = true;
    const init = async () => {
      const { fetchCatalog: doFetchCatalog, fetchPresets: doFetchPresets, applyPreset: doApplyPreset, createFullSelection: doCreateFullSelection } = latestRef.current;
      const [catData, presetsData] = await Promise.all([doFetchCatalog(), doFetchPresets()]);
      if (!isMounted || !catData) return;

      // Check if user has a default preset
      const defaultPreset = (presetsData || []).find((p) => p.isDefault);
      if (defaultPreset) {
        doApplyPreset(defaultPreset);
        return;
      }

      // Check localStorage for last selection in this session
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && typeof parsed === "object") {
            const reconstructed = {};
            if (Array.isArray(parsed.types)) {
              parsed.types.forEach((item) => {
                if (item?.key) reconstructed[item.key] = item.variants;
              });
            }
            setSelectedTypes(sanitizeTypesSelection(reconstructed, catData.types));
            setCustomColumns(Array.isArray(parsed.customColumns) ? parsed.customColumns : []);
            setActivePresetId(parsed.activePresetId || null);
            setIsModified(Boolean(parsed.isModified));
            return;
          }
        }
      } catch (e) {}

      // Fallback: Full template
      setSelectedTypes(doCreateFullSelection(catData.types));
      setCustomColumns([]);
      setActivePresetId(null);
      setIsModified(false);
    };

    init();

    return () => {
      isMounted = false;
    };
    // Intentionally depends only on isOpen — see hasLoadedRef/latestRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Persist working selection to localStorage
  useEffect(() => {
    if (!isOpen || Object.keys(selectedTypes).length === 0) return;
    try {
      const payload = {
        activePresetId,
        isModified,
        types: Object.entries(selectedTypes).map(([key, variants]) => ({ key, variants })),
        customColumns,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {}
  }, [isOpen, selectedTypes, customColumns, activePresetId, isModified]);

  // Toggle all variants for a type (binary/tri-state row checkbox)
  const toggleType = useCallback((typeKey) => {
    const typeDef = catalog.types.find((t) => t.key === typeKey);
    const available = typeDef?.availableVariants || ["net", "gross"];

    setSelectedTypes((prev) => {
      const current = prev[typeKey] || [];
      const next = { ...prev };
      if (current.length > 0) {
        // Deselect completely
        delete next[typeKey];
      } else {
        // Select all available variants
        next[typeKey] = [...available];
      }
      return next;
    });
    setIsModified(true);
  }, [catalog.types]);

  // Toggle a single variant pill for a type
  const toggleTypeVariant = useCallback((typeKey, variant) => {
    const typeDef = catalog.types.find((t) => t.key === typeKey);
    const available = new Set(typeDef?.availableVariants || ["net", "gross"]);
    if (!available.has(variant)) return;

    setSelectedTypes((prev) => {
      const current = prev[typeKey] || [];
      const set = new Set(current);
      if (set.has(variant)) {
        set.delete(variant);
      } else {
        set.add(variant);
      }
      const next = { ...prev };
      if (set.size === 0) {
        delete next[typeKey];
      } else {
        next[typeKey] = Array.from(set);
      }
      return next;
    });
    setIsModified(true);
  }, [catalog.types]);

  // Global variant toggle ("Show: Net / Gross / Days Net / Days Gross")
  const toggleGlobalVariant = useCallback((variant) => {
    setSelectedTypes((prev) => {
      // Determine if this variant is currently active on any selected type
      let anyHasVariant = false;
      Object.values(prev).forEach((vList) => {
        if (vList.includes(variant)) anyHasVariant = true;
      });

      const turnOn = !anyHasVariant;
      const next = {};

      catalog.types.forEach((typeDef) => {
        const currentVariants = prev[typeDef.key] || [];
        const isQtyOnly = typeDef.availableVariants?.length === 2 && !typeDef.availableVariants.includes("days_net");

        if (turnOn) {
          // If turning on, add variant if supported by typeDef
          if (typeDef.availableVariants?.includes(variant)) {
            const s = new Set(currentVariants);
            s.add(variant);
            next[typeDef.key] = Array.from(s);
          } else if (currentVariants.length > 0) {
            next[typeDef.key] = [...currentVariants];
          }
        } else {
          // If turning off, remove variant from types
          const s = new Set(currentVariants);
          s.delete(variant);
          if (s.size > 0) {
            next[typeDef.key] = Array.from(s);
          }
        }
      });

      return next;
    });
    setIsModified(true);
  }, [catalog.types]);

  // Tri-state group toggle (e.g. all allowances, all deductions, or all filtered)
  const toggleGroup = useCallback((kind, checkAll) => {
    setSelectedTypes((prev) => {
      const next = { ...prev };
      catalog.types
        .filter((t) => t.kind === kind)
        .forEach((t) => {
          if (checkAll) {
            next[t.key] = [...(t.availableVariants || ["net", "gross"])];
          } else {
            delete next[t.key];
          }
        });
      return next;
    });
    setIsModified(true);
  }, [catalog.types]);

  // Select all or clear all filtered types
  const toggleAllFiltered = useCallback((selectAll, filteredTypes) => {
    setSelectedTypes((prev) => {
      const next = { ...prev };
      filteredTypes.forEach((t) => {
        if (selectAll) {
          next[t.key] = [...(t.availableVariants || ["net", "gross"])];
        } else {
          delete next[t.key];
        }
      });
      return next;
    });
    setIsModified(true);
  }, []);

  // Custom column add
  const addCustomColumn = useCallback((col) => {
    setCustomColumns((prev) => [...prev, col]);
    setIsModified(true);
  }, []);

  // Custom column remove
  const removeCustomColumn = useCallback((index) => {
    setCustomColumns((prev) => prev.filter((_, i) => i !== index));
    setIsModified(true);
  }, []);

  // Preset CRUD operations
  const saveAsNewPreset = useCallback(async ({ name, description, scope, isDefault }) => {
    const typesArray = Object.entries(selectedTypes).map(([key, variants]) => ({ key, variants }));
    const selection = {
      schemaVersion: 1,
      types: typesArray,
      customColumns,
      options: { includeSampleRows: true, styledRowCount: 200 },
      presetName: name,
    };

    const res = await apiFetch(`${API_BASE_URL}/payrollX/template-presets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        description,
        scope: scope || "PERSONAL",
        isDefault: Boolean(isDefault),
        selection,
      }),
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || "Failed to save preset");
    }

    await fetchPresets();
    setActivePresetId(json.data.id);
    setIsModified(false);
    showAlert(t("success") || "Success", t("templatePresetSaved") || "Template preset saved", "success");
    return json.data;
  }, [API_BASE_URL, selectedTypes, customColumns, fetchPresets, showAlert, t]);

  const updatePreset = useCallback(async (presetId, updates = {}) => {
    const typesArray = Object.entries(selectedTypes).map(([key, variants]) => ({ key, variants }));
    const selection = {
      schemaVersion: 1,
      types: typesArray,
      customColumns,
      options: { includeSampleRows: true, styledRowCount: 200 },
    };

    const body = {
      ...updates,
      selection: updates.selection || selection,
    };

    const res = await apiFetch(`${API_BASE_URL}/payrollX/template-presets/${presetId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || "Failed to update preset");
    }

    await fetchPresets();
    setIsModified(false);
    showAlert(t("success") || "Success", t("templatePresetUpdated") || "Template preset updated", "success");
    return json.data;
  }, [API_BASE_URL, selectedTypes, customColumns, fetchPresets, showAlert, t]);

  const deletePreset = useCallback(async (presetId) => {
    const res = await apiFetch(`${API_BASE_URL}/payrollX/template-presets/${presetId}`, {
      method: "DELETE",
    });

    if (!res.ok) {
      const json = await res.json();
      throw new Error(json.error || "Failed to delete preset");
    }

    await fetchPresets();
    if (activePresetId === presetId) {
      resetToFullTemplate();
    }
    showAlert(t("success") || "Success", t("templatePresetDeleted") || "Template preset deleted", "success");
  }, [API_BASE_URL, activePresetId, fetchPresets, resetToFullTemplate, showAlert, t]);

  // Export handlers
  const exportFullTemplate = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/payrollX/bulk/allowances-deductions-template`, {
        method: "GET",
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "allowances_deductions_template.xlsx";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting full template:", err);
      showAlert(t("error") || "Error", t("errorExportingTemplate") || "Could not build the template.", "error");
    } finally {
      setIsExporting(false);
    }
  }, [API_BASE_URL, isExporting, showAlert, t]);

  const exportFiltered = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const activePreset = presets.find((p) => p.id === activePresetId);
      const typesArray = Object.entries(selectedTypes).map(([key, variants]) => ({ key, variants }));

      const payload = {
        schemaVersion: 1,
        types: typesArray,
        customColumns,
        options: { includeSampleRows: true, styledRowCount: 200 },
        presetName: activePreset ? activePreset.name : undefined,
      };

      const response = await apiFetch(`${API_BASE_URL}/payrollX/bulk/allowances-deductions-template`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.error || `HTTP ${response.status}`);
      }

      // Read Content-Disposition header for filename if available
      let filename = "allowances_deductions_template.xlsx";
      const disposition = response.headers.get("content-disposition");
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename=["']?([^"';]+)["']?/);
        if (match && match[1]) filename = match[1];
      } else if (activePreset?.name) {
        const slug = activePreset.name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
        if (slug) filename = `allowances_deductions_${slug}.xlsx`;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting filtered template:", err);
      showAlert(t("error") || "Error", err.message || t("errorExportingTemplate") || "Could not build the template.", "error");
    } finally {
      setIsExporting(false);
    }
  }, [API_BASE_URL, activePresetId, customColumns, isExporting, presets, selectedTypes, showAlert, t]);

  // Derived summaries & totals
  const totals = useMemo(() => {
    let allowanceCols = 0;
    let deductionCols = 0;

    // From base types. Selections are clipped to each type's CURRENT
    // availableVariants — a selection restored from localStorage or a
    // saved preset can carry variants (e.g. days_net/days_gross) that a
    // later catalog change made invalid for that type (like Overtime
    // Allowance moving from 4 variants to 2 once it was merged with the
    // linked Overtime Days/Hours columns) — without this clip those
    // phantom entries would silently inflate the totals shown here.
    catalog.types.forEach((typeDef) => {
      const rawSelectedVariants = selectedTypes[typeDef.key] || [];
      const validVariants = new Set(typeDef.availableVariants || []);
      const selectedVariants = rawSelectedVariants.filter((v) => validVariants.has(v));
      if (typeDef.kind === "allowance") {
        allowanceCols += selectedVariants.length;
      } else if (typeDef.kind === "deduction") {
        deductionCols += selectedVariants.length;
      }
    });

    // From custom columns
    customColumns.forEach((c) => {
      const count = (c.variants || []).length;
      if (c.kind === "allowance") {
        allowanceCols += count;
      } else {
        deductionCols += count;
      }
    });

    const totalSelected = 2 + allowanceCols + deductionCols; // 2 locked
    const maxTotalColumns = catalog.totals?.columns || 186;

    const selectedTypesCount = Object.keys(selectedTypes).length + customColumns.length;
    const totalTypesCount = catalog.types.length + customColumns.length;

    return {
      totalSelected,
      maxTotalColumns,
      allowanceCols,
      deductionCols,
      selectedTypesCount,
      totalTypesCount,
    };
  }, [catalog, selectedTypes, customColumns]);

  // Types folded into another type's row via linkedQuantities (e.g.
  // "Overtime Hours"/"Overtime Days" appear as extra pills on the "Overtime
  // Allowance" row) are hidden from the list here — they stay fully usable
  // through those pills, and catalog.types itself is left untouched so
  // "select all" / full-template default selection still includes them.
  const mergedTargetKeys = useMemo(() => {
    const s = new Set();
    (catalog.types || []).forEach((tDef) => {
      (tDef.linkedQuantities || []).forEach((lq) => s.add(lq.targetKey));
    });
    return s;
  }, [catalog.types]);

  // Filtered types according to searchQuery and kindFilter
  const filteredTypes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return catalog.types.filter((tDef) => {
      if (mergedTargetKeys.has(tDef.key)) return false;
      if (kindFilter !== "all" && tDef.kind !== kindFilter) return false;
      if (!q) return true;
      const label = (tDef.label || "").toLowerCase();
      const key = (tDef.key || "").toLowerCase();
      const derived = (tDef.derivedType || "").toLowerCase();
      const translated = (tDef.labelKey && t && t(tDef.labelKey) ? t(tDef.labelKey) : "").toLowerCase();
      return label.includes(q) || key.includes(q) || derived.includes(q) || translated.includes(q);
    });
  }, [catalog.types, mergedTargetKeys, searchQuery, kindFilter, t]);

  return {
    catalog,
    isLoadingCatalog,
    catalogError,
    presets,
    isLoadingPresets,
    activePresetId,
    activePreset: presets.find((p) => p.id === activePresetId) || null,
    selectedTypes,
    customColumns,
    isModified,
    isExporting,
    searchQuery,
    setSearchQuery,
    kindFilter,
    setKindFilter,
    totals,
    filteredTypes,
    canShareCompany,
    toggleType,
    toggleTypeVariant,
    toggleGlobalVariant,
    toggleGroup,
    toggleAllFiltered,
    addCustomColumn,
    removeCustomColumn,
    resetToFullTemplate,
    applyPreset,
    saveAsNewPreset,
    updatePreset,
    deletePreset,
    exportFullTemplate,
    exportFiltered,
  };
};
